// Tenant-facing Workflow Engine RTK Query API — Engagements (ENG) + Workflow execution (WFL).
// This is the RUNTIME/execution side (an engagement's live position in its workflow, available
// transitions, approvals) driven entirely by SAM tool calls — NOT the platform-admin workflow
// *definition* authoring API (see workflowApi.ts, unrelated and untouched by this file).
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';

// ─── Response envelope (mirrors workspaceApiExtensions / workflowApi) ──────────
interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
}
interface ApiListResponse<T> extends ApiResponse<T[]> {
  total: number;
}

// ─── Domain types ───────────────────────────────────────────────────────────

export interface DisplayLabels {
  stage: string;
  process: string;
  status: string;
}

export interface EngagementOut {
  engagement_id: string;
  tenant_id: string;
  property_id: string;
  engagement_type: string;
  title: string;
  assigned_user_id?: string | null;
  // Sprint 6 JOB-FEAT-01 — project_manager_id is set by POST /engagements/{id}/assign-pm (api-service app/domain/jobs/service.py).
  holder_id?: string | null;
  holder_name?: string | null;
  sales_agent_id?: string | null;
  sales_agent_name?: string | null;
  sales_agent_phone?: string | null;
  project_manager_id?: string | null;
  project_manager_name?: string | null;
  project_manager_phone?: string | null;
  assigned_user_name?: string | null;
  assigned_user_phone?: string | null;
  data: Record<string, unknown>;
  access_policy_override?: AccessPolicy | null;
  closed_early?: { closure_type: 'closed_lost' | 'future_follow_up'; reason: string; closed_by: string; closed_at: string } | null;
  current_status_code: string;
  current_process_code: string;
  current_stage_code: string;
  display_labels: DisplayLabels;
  created_by?: string | null;
  created_on: string;
  updated_on: string;
}

export interface EngagementActivity {
  activity_type: string;
  actor_user_id: string | null;
  actor_name: string | null;
  detail: Record<string, unknown> | null;
  occurred_at: string;
}

export interface EngagementWorkflowStateOut {
  state_id: string;
  engagement_id: string;
  engagement_type: string;
  workflow_definition_id: string;
  definition_version: number;
  current_status_code: string;
  current_process_code: string;
  current_stage_code: string;
  operational_type: string;
  lifecycle_role: string;
  display_labels: DisplayLabels;
  lock_version: number;
  started_at: string;
  last_transition_at?: string | null;
  last_transition_by?: string | null;
  last_transition_channel?: string | null;
  pending_approvals: unknown[];
  acknowledged_warnings: string[];
  // G2/G3 — flag-based hold for processes with no dedicated on-hold status of their own.
  on_hold: boolean;
  on_hold_reason?: string | null;
  on_hold_since?: string | null;
  created_on: string;
  updated_on: string;
  // WFL business-module linking, POST side (module_displays) — every module_code bound to
  // current_status_code via a status_entry module_displays entry, in definition order. A status
  // can carry more than one (e.g. a meeting card AND an interactive capture tool at the same
  // status). WorkflowCard.tsx looks each code up in BUSINESS_MODULE_DISPLAY_COMPONENTS and
  // renders every match — an unregistered code is just skipped, no crash.
  active_display_modules: string[];
}

/** What actually clears a blocked transition, derived server-side from the blocking gate's
 *  condition_type (app.domain.workflows.service._resolution_hint) — never hand-authored, so it
 *  can't drift out of sync with the check itself. `fields` names the exact engagement.data key(s)
 *  to set via updateEngagementData for 'update_engagement_data'. */
export interface TransitionResolution {
  action_type: 'add_contact_participant' | 'update_engagement_data' | 'open_media' | 'open_measurement' | 'open_tasks' | 'open_approval' | 'open_estimate' | 'business_capture';
  fields?: string[];
  task_source?: string | null;
  approval_type?: string | null;
  required_roles?: string[];
  // Tenant's own label for required_roles (roles can be renamed per-tenant) — only present for
  // action_type='open_approval'.
  required_role_label?: string | null;
  // Governance-action registry key (GET /workflows/governance-actions) this gate is bound to —
  // only present for action_type='business_capture'. The gate author opted in via
  // condition_config.capture_ui_enabled (WorkflowService._resolution_hint); whether a form
  // actually renders additionally depends on the frontend having a matching entry in
  // WorkflowCard.tsx's BUSINESS_CAPTURE_COMPONENTS — both sides must agree.
  action_key?: string;
}

export interface AvailableTransition {
  transition_code: string;
  action_label: string;
  category: string;
  requires_reason: boolean;
  executable: boolean;
  reason: string | null;
  /** The blocking gate's own "what to do next" sentence — distinct from `reason`, which says
   *  what is wrong. Null unless a governance gate did the blocking (an authority denial has no
   *  next action the user can take themselves). Was stored on every seeded gate and surfaced
   *  nowhere until now, while SAM's prompt already instructed the model to use it. */
  required_next_action?: string | null;
  resolution?: TransitionResolution | null;
  // True only when blocked because the current user lacks the role/authority for this
  // transition — as opposed to a governance-gate or on-hold/closed-early block, which is a
  // blockage on the engagement itself (e.g. missing required data) rather than on the user.
  // The frontend hides the button entirely for the former, and keeps it visible (with the
  // reason/resolution hint) for the latter.
  permission_denied?: boolean;
}

/** POST .../transitions/{code}/execute result — either it went through (possibly with a failed
 *  non-mandatory hook) or a warning-classified gate needs an explicit acknowledge-and-retry.
 *  Blocked (409) / denied (403) / stale-state (409) responses surface as a rejected mutation
 *  instead (standard `{success:false, message_code, message, detail}` envelope). */
export type ExecuteTransitionResult =
  | {
      status: 'executed';
      mandatory_hook_failed: boolean;
      state: EngagementWorkflowStateOut;
      available_transitions: AvailableTransition[];
    }
  | { status: 'warning_required'; message: string; gate_code: string };

export interface WorkflowStatusChip {
  label: string;
  tone: 'positive' | 'neutral' | 'warning';
}

/** Real LLM-authored (gpt-4o-mini) status update for the "SAM" card at the top of the workflow
 *  card — see api-service app/domain/workflows/narration.py. Narrates the same transition/gate/
 *  authority data listAvailableTransitions already exposes; not a second source of truth. */
export interface WorkflowStatusNarration {
  summary: string;
  chips: WorkflowStatusChip[];
}

// ─── Workflow customization (tenant STRUCTURE editor) ────────────────────────
// A SECOND, DISTINCT surface from the execution types above: this is the tenant's whole workflow
// STRUCTURE (all stages -> processes -> statuses -> transitions -> gates/hooks), with no engagement
// in the picture — edited by a tenant_admin via SAM's workflow-customization tools
// (get_workflow_customization / toggle_workflow_item / rename_workflow_label / add_workflow_* /
// set_transition_authority). NOT the platform-admin workflow *definition* authoring API
// (workflowApi.ts, unrelated and untouched by this file).

export interface AccessPolicy {
  allowed_roles: string[];
  assignment_required: boolean;
}

export interface StageDef {
  stage_code: string;
  name: string;
  display_order: number;
  description?: string;
  is_custom: boolean;
  tenant_label_renamable: boolean;
  access_policy?: AccessPolicy | null;
}

export interface ProcessDef {
  process_code: string;
  stage_code: string;
  name: string;
  display_order: number;
  description?: string;
  is_custom: boolean;
  tenant_label_renamable: boolean;
  access_policy?: AccessPolicy | null;
}

export interface StatusDef {
  status_code: string;
  process_code: string;
  name: string;
  display_order: number;
  lifecycle_role: 'entry' | 'intermediate' | 'terminal';
  operational_type: 'normal' | 'on_hold' | 'closed' | 'cancelled' | 'rejected';
  is_optional: boolean;
  is_custom: boolean;
  is_active: boolean;
  tenant_label_renamable: boolean;
  description?: string;
}

export interface TransitionDef {
  transition_code: string;
  source_status_code: string;
  target_status_code: string;
  action_label: string;
  category: string;
  requires_reason: boolean;
  is_optional: boolean;
  is_custom: boolean;
  is_active: boolean;
  tenant_label_renamable: boolean;
  description?: string;
}

export interface GateDef {
  gate_code: string;
  scope: string;
  target_ref: string;
  classification: 'blocking' | 'warning';
  condition_type: string;
  is_optional: boolean;
  is_custom: boolean;
  is_active: boolean;
  description?: string;
  approver_role?: string | null;
  prohibit_self_approval?: boolean;
}

export interface HookDef {
  hook_code: string;
  trigger_point: string;
  trigger_ref: string;
  action_type: string;
  is_mandatory: boolean;
  is_optional: boolean;
  is_custom: boolean;
  is_active: boolean;
  description?: string;
}

export interface EntryPointDef {
  status_code: string;
  engagement_types: string[];
  description?: string;
}

export interface AuthorityRuleDef {
  rule_code: string;
  authority_type: string;
  target_refs: string[];
  allowed_roles: string[];
  assignment_scope?: string;
  self_approval_allowed?: boolean;
  system_allowed?: boolean;
  description?: string;
}

export interface WorkflowCustomizationOut {
  /** "tenant_fork" once the tenant has customized anything; "platform" = read-only fallback view
   *  of the platform definition (tenant hasn't forked it yet). */
  source: 'tenant_fork' | 'platform';
  customization_id: string | null;
  definition_id: string | null;
  version: number | null;
  tenant_id: string | null;
  name: string;
  description: string;
  engagement_types: string[];
  default_access_policy?: AccessPolicy | null;
  stages: StageDef[];
  processes: ProcessDef[];
  statuses: StatusDef[];
  entry_points: EntryPointDef[];
  transitions: TransitionDef[];
  governance_gates: GateDef[];
  automation_hooks: HookDef[];
  authority_rules: AuthorityRuleDef[];
  /** Which business-module card shows at which status (e.g. the Billing panel). */
  module_displays?: ModuleDisplayDef[];
}

export interface ModuleDisplayDef {
  binding_code: string;
  scope: 'status_entry';
  target_ref: string;
  module_code: string;
  is_active: boolean;
  is_optional?: boolean;
  is_custom?: boolean;
  description?: string;
}

/** POST /stages, /processes, /statuses, /transitions responses — the full updated structure plus
 *  the generated code of the newly-created item. */
export interface WorkflowCustomizationCreated extends WorkflowCustomizationOut {
  created_code: string;
}

/** Summary of a tenant's workflow type — returned by list_company_workflows for the overview card. */
export interface WorkflowSummary {
  workflow_id: string | null;
  name: string;
  description: string;
  engagement_types: string[];
  stage_count: number;
  process_count: number;
  status_count: number;
  is_customized: boolean;
  source: 'tenant_fork' | 'platform';
}

export interface ApprovalRequestOut {
  approval_request_id: string;
  approval_group_id: string;
  engagement_id: string;
  approval_type: string;
  approval_strategy: string;
  status: string;
  requested_by: string;
  requested_at: string;
  assigned_approver_user_id?: string | null;
  assigned_approver_role?: string | null;
  allowed_approver_roles: string[];
  decided_at?: string | null;
  decided_by?: string | null;
  decision_comments?: string | null;
  created_on: string;
  updated_on: string;
  // Stage the approval was requested for (snapshot at creation time) — undefined for requests
  // created before this field existed. Used to scope the engagement-view list to the current
  // stage instead of showing the engagement's full approval history indefinitely.
  stage_code?: string | null;
  // Status the engagement reached via the first transition after this approval was decided —
  // null until that transition happens. Lets the UI keep a decided approval visible while the
  // engagement is still on the step it resolved to, and drop it once that step is left behind.
  resolved_status_code?: string | null;
  // Only meaningful from listEngagementApprovals — whether the CURRENT user is authorized to
  // decide this approval. listPendingApprovals is already actor-scoped, so it's always true there.
  can_decide: boolean;
  // Tenant's own label for the required approver role(s) — only populated by
  // listEngagementApprovals, so any viewer can see who needs to act.
  required_role_label?: string | null;
  // Only populated by listPendingApprovals (the personal "my approvals" feed) — gives the row
  // enough context to identify the job without opening it first.
  property_address?: string | null;
  engagement_title?: string | null;
}

// ─── helper ─────────────────────────────────────────────────────────────────
function toQs(params: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
}

// ─── API slice ──────────────────────────────────────────────────────────────

/** `?workflow_id=` for the tenant workflow-customization endpoints; empty for the default workflow. */
const wfQuery = (workflowId?: string | void) => (workflowId ? `?workflow_id=${encodeURIComponent(workflowId)}` : '');

export interface ConfigurableWorkflow {
  workflow_id: string;
  name: string;
  description: string;
  engagement_types: string[];
  is_forked: boolean;
  is_customized: boolean;
  is_default: boolean;
}

export const workflowEngineApi = createApi({
  reducerPath: 'workflowEngineApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Engagement', 'EngagementList', 'EngagementActivity', 'EngagementWorkflowState', 'AvailableTransitions', 'PendingApprovals', 'WorkflowCustomization'],
  endpoints: (builder) => ({

    // ── Engagements ─────────────────────────────────────────────────────
    listEngagements: builder.query<
      { data: EngagementOut[]; total: number },
      { propertyId?: string; skip?: number; limit?: number } | void
    >({
      query: (p) => `/api/v1/engagements${toQs({ property_id: p?.propertyId, skip: p?.skip, limit: p?.limit })}`,
      transformResponse: (res: ApiListResponse<EngagementOut>) => ({ data: res.data, total: res.total }),
      providesTags: ['EngagementList'],
    }),

    getEngagementById: builder.query<EngagementOut, string>({
      query: (id) => `/api/v1/engagements/${id}`,
      transformResponse: (res: ApiResponse<EngagementOut>) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Engagement', id }],
    }),

    getEngagementActivity: builder.query<EngagementActivity[], string>({
      query: (id) => `/api/v1/engagements/${id}/activity`,
      transformResponse: (res: ApiListResponse<EngagementActivity>) => res.data,
      // Dedicated tag so only activity-writing mutations trigger a refetch (not every
      // Engagement update).  Mutations that write to the activity log must invalidate this.
      providesTags: (_r, _e, id) => [{ type: 'EngagementActivity', id }],
    }),

    createEngagement: builder.mutation<
      EngagementOut,
      // Engagement name is never client-supplied — the server auto-generates it as
      // "<Engagement Type label> <serial no>" (see api-service EngagementsService.create).
      { propertyId: string; engagementType: string; assignedUserId?: string; data?: Record<string, unknown> }
    >({
      query: ({ propertyId, engagementType, assignedUserId, data }) => ({
        url: '/api/v1/engagements',
        method: 'POST',
        body: { property_id: propertyId, engagement_type: engagementType, assigned_user_id: assignedUserId, data },
      }),
      transformResponse: (res: ApiResponse<EngagementOut>) => res.data,
      invalidatesTags: ['EngagementList'],
    }),

    updateEngagementData: builder.mutation<EngagementOut, { id: string; data: Record<string, unknown> }>({
      query: ({ id, data }) => ({ url: `/api/v1/engagements/${id}/data`, method: 'PUT', body: { data } }),
      transformResponse: (res: ApiResponse<EngagementOut>) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Engagement', id }, 'EngagementList'],
    }),

    // G1 (Close Engagement Early) — Owner-only, engagement-level outcome field (not a status
    // move), works from any active status. Permanent — see WorkflowService's closed_early guards.
    closeEngagementEarly: builder.mutation<
      EngagementOut,
      { id: string; closureType: 'closed_lost' | 'future_follow_up'; reason: string }
    >({
      query: ({ id, closureType, reason }) => ({
        url: `/api/v1/engagements/${id}/close-early`,
        method: 'POST',
        body: { closure_type: closureType, reason },
      }),
      transformResponse: (res: ApiResponse<EngagementOut>) => res.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Engagement', id }, 'EngagementList',
        { type: 'EngagementWorkflowState', id }, { type: 'AvailableTransitions', id },
        { type: 'EngagementActivity', id },
      ],
    }),

    // G4 (Reassign Holder) — transfers the Engagement Holder to a different user. Owner-only
    // (enforced server-side), works from any status — no workflow move involved.
    reassignEngagementHolder: builder.mutation<EngagementOut, { id: string; newHolderUserId: string; reason?: string }>({
      query: ({ id, newHolderUserId, reason }) => ({
        url: `/api/v1/engagements/${id}/holder`,
        method: 'PUT',
        body: { new_holder_user_id: newHolderUserId, reason },
      }),
      transformResponse: (res: ApiResponse<EngagementOut>) => res.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Engagement', id }, 'EngagementList',
        { type: 'EngagementActivity', id },
      ],
    }),

    // Per-instance override — narrows access for THIS engagement only, independent of the
    // workflow-level default/stage/process access_policy (see setWorkflowAccessPolicy below).
    setEngagementAccessPolicy: builder.mutation<
      EngagementOut,
      { id: string; allowedRoles: string[]; assignmentRequired: boolean }
    >({
      query: ({ id, allowedRoles, assignmentRequired }) => ({
        url: `/api/v1/engagements/${id}/access-policy`,
        method: 'PUT',
        body: { allowed_roles: allowedRoles, assignment_required: assignmentRequired },
      }),
      transformResponse: (res: ApiResponse<EngagementOut>) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Engagement', id }, 'EngagementList'],
    }),

    // ── Workflow execution ──────────────────────────────────────────────
    getEngagementWorkflowState: builder.query<EngagementWorkflowStateOut, string>({
      query: (engagementId) => `/api/v1/workflows/engagements/${engagementId}/state`,
      transformResponse: (res: ApiResponse<EngagementWorkflowStateOut>) => res.data,
      providesTags: (_r, _e, engagementId) => [{ type: 'EngagementWorkflowState', id: engagementId }],
    }),

    listAvailableTransitions: builder.query<AvailableTransition[], string>({
      query: (engagementId) => `/api/v1/workflows/engagements/${engagementId}/transitions`,
      transformResponse: (res: ApiListResponse<AvailableTransition>) => res.data,
      providesTags: (_r, _e, engagementId) => [{ type: 'AvailableTransitions', id: engagementId }],
    }),

    getWorkflowStatusNarration: builder.query<WorkflowStatusNarration, { engagementId: string; statusCode?: string }>({
      // `statusCode` isn't part of the URL — it only differentiates the RTK Query cache key, so
      // a real status transition (a different statusCode for the same engagementId) naturally
      // triggers a fresh LLM call instead of serving the previous status's stale narration.
      query: ({ engagementId }) => `/api/v1/workflows/engagements/${engagementId}/status-narration`,
      transformResponse: (res: ApiResponse<WorkflowStatusNarration>) => res.data,
      // Reuses the AvailableTransitions tag so executing a transition (which already invalidates
      // that tag) also refreshes the narration, on top of the automatic refetch from a changed
      // statusCode arg.
      providesTags: (_r, _e, { engagementId }) => [{ type: 'AvailableTransitions', id: engagementId }],
    }),

    executeWorkflowTransition: builder.mutation<
      ExecuteTransitionResult,
      { engagementId: string; transitionCode: string; reason?: string; acknowledgedWarnings?: string[] }
    >({
      query: ({ engagementId, transitionCode, reason, acknowledgedWarnings }) => ({
        url: `/api/v1/workflows/engagements/${engagementId}/transitions/${transitionCode}/execute`,
        method: 'POST',
        body: { reason, acknowledged_warnings: acknowledgedWarnings },
      }),
      transformResponse: (res: ApiResponse<ExecuteTransitionResult>) => res.data,
      // Re-fetch the engagement's state + transitions (and the engagements list, since its
      // display_labels/current_*_code change too) after a successful execute.
      invalidatesTags: (result, _e, { engagementId }) =>
        result?.status === 'executed'
          ? [
              { type: 'EngagementWorkflowState', id: engagementId },
              { type: 'AvailableTransitions', id: engagementId },
              { type: 'Engagement', id: engagementId },
              { type: 'EngagementActivity', id: engagementId },
              'EngagementList',
            ]
          : [],
    }),

    // ── On-Hold flag (G2/G3) ─────────────────────────────────────────────
    putEngagementOnHold: builder.mutation<EngagementWorkflowStateOut, { engagementId: string; reason: string }>({
      query: ({ engagementId, reason }) => ({
        url: `/api/v1/workflows/engagements/${engagementId}/hold`,
        method: 'POST',
        body: { reason },
      }),
      transformResponse: (res: ApiResponse<EngagementWorkflowStateOut>) => res.data,
      invalidatesTags: (_r, _e, { engagementId }) => [
        { type: 'EngagementWorkflowState', id: engagementId },
        { type: 'AvailableTransitions', id: engagementId },
        { type: 'EngagementActivity', id: engagementId },
      ],
    }),

    resumeEngagement: builder.mutation<EngagementWorkflowStateOut, string>({
      query: (engagementId) => ({ url: `/api/v1/workflows/engagements/${engagementId}/resume`, method: 'POST' }),
      transformResponse: (res: ApiResponse<EngagementWorkflowStateOut>) => res.data,
      invalidatesTags: (_r, _e, engagementId) => [
        { type: 'EngagementWorkflowState', id: engagementId },
        { type: 'AvailableTransitions', id: engagementId },
        { type: 'EngagementActivity', id: engagementId },
      ],
    }),

    // ── Approvals ────────────────────────────────────────────────────────
    listPendingApprovals: builder.query<{ data: ApprovalRequestOut[]; total: number }, void>({
      query: () => '/api/v1/workflows/approvals?assigned_to_me=true',
      transformResponse: (res: ApiListResponse<ApprovalRequestOut>) => ({ data: res.data, total: res.total }),
      providesTags: ['PendingApprovals'],
    }),

    // Every pending approval on the engagement, visible to any viewer — not just the actor-scoped
    // "my approvals" feed above. Each row's can_decide tells the UI who gets Approve/Reject.
    listEngagementApprovals: builder.query<{ data: ApprovalRequestOut[]; total: number }, string>({
      query: (engagementId) => `/api/v1/workflows/engagements/${engagementId}/approvals`,
      transformResponse: (res: ApiListResponse<ApprovalRequestOut>) => ({ data: res.data, total: res.total }),
      providesTags: (_r, _e, engagementId) => [{ type: 'PendingApprovals', id: engagementId }],
    }),

    // Single-approval lookup for the deep-link page a REQUEST_SUBMITTED notification routes to
    // (action_route=/workflow/approvals/{approvalId}) — server enforces the same eligibility
    // check as decide (assigned approver or a matching allowed_approver_role), 403 otherwise.
    getApprovalById: builder.query<ApprovalRequestOut, string>({
      query: (approvalId) => `/api/v1/workflows/approvals/${approvalId}`,
      transformResponse: (res: ApiResponse<ApprovalRequestOut>) => res.data,
      providesTags: (_r, _e, approvalId) => [{ type: 'PendingApprovals', id: approvalId }],
    }),

    decideApproval: builder.mutation<ApprovalRequestOut, { approvalId: string; decision: 'approved' | 'rejected'; comments?: string }>({
      query: ({ approvalId, decision, comments }) => ({
        url: `/api/v1/workflows/approvals/${approvalId}/decide`,
        method: 'POST',
        body: { decision, comments },
      }),
      transformResponse: (res: ApiResponse<ApprovalRequestOut>) => res.data,
      // A decided approval can flip the engagement's workflow state (approval gates resolve), so
      // invalidate that engagement's state/transitions too, alongside the approvals list.
      invalidatesTags: (result) => [
        'PendingApprovals',
        ...(result ? [
          { type: 'PendingApprovals' as const, id: result.approval_request_id },
          { type: 'EngagementWorkflowState' as const, id: result.engagement_id },
          { type: 'AvailableTransitions' as const, id: result.engagement_id },
          { type: 'EngagementActivity' as const, id: result.engagement_id },
        ] : []),
      ],
    }),

    reassignApproval: builder.mutation<ApprovalRequestOut, { approvalId: string; toUserId?: string; toRole?: string; reason: string }>({
      query: ({ approvalId, toUserId, toRole, reason }) => ({
        url: `/api/v1/workflows/approvals/${approvalId}/reassign`,
        method: 'POST',
        body: { to_user_id: toUserId, to_role: toRole, reason },
      }),
      transformResponse: (res: ApiResponse<ApprovalRequestOut>) => res.data,
      invalidatesTags: ['PendingApprovals'],
    }),

    // ── Workflow customization (tenant STRUCTURE editor) ────────────────
    // Platform workflows this tenant can configure (feeds the Settings workflow selector).
    listConfigurableWorkflows: builder.query<ConfigurableWorkflow[], void>({
      query: () => '/api/v1/workflows/customization/workflows',
      transformResponse: (res: ApiResponse<ConfigurableWorkflow[]>) => res.data,
      providesTags: ['WorkflowCustomization'],
    }),

    // `workflowId` omitted = the tenant's default workflow (every existing caller).
    getWorkflowCustomization: builder.query<WorkflowCustomizationOut, string | void>({
      query: (workflowId) => `/api/v1/workflows/customization${wfQuery(workflowId)}`,
      transformResponse: (res: ApiResponse<WorkflowCustomizationOut>) => res.data,
      providesTags: ['WorkflowCustomization'],
    }),

    toggleWorkflowItem: builder.mutation<
      WorkflowCustomizationOut,
      { workflowId?: string; itemType: 'status' | 'transition' | 'gate' | 'hook' | 'module_display'; itemCode: string; active: boolean }
    >({
      query: ({ workflowId, itemType, itemCode, active }) => ({
        url: `/api/v1/workflows/customization/toggle${wfQuery(workflowId)}`,
        method: 'POST',
        body: { item_type: itemType, item_code: itemCode, active },
      }),
      transformResponse: (res: ApiResponse<WorkflowCustomizationOut>) => res.data,
      // A toggled status / transition / stage panel changes what an open job's workflow card shows
      // (e.g. the Billing panel), so its state is refetched too.
      invalidatesTags: ['WorkflowCustomization', 'EngagementWorkflowState', 'AvailableTransitions'],
    }),

    renameWorkflowLabel: builder.mutation<
      WorkflowCustomizationOut,
      { workflowId?: string;  itemType: 'stage' | 'process' | 'status' | 'transition'; itemCode: string; newLabel: string }
    >({
      query: ({ workflowId, itemType, itemCode, newLabel }) => ({
        url: `/api/v1/workflows/customization/rename${wfQuery(workflowId)}`,
        method: 'POST',
        body: { item_type: itemType, item_code: itemCode, new_label: newLabel },
      }),
      transformResponse: (res: ApiResponse<WorkflowCustomizationOut>) => res.data,
      invalidatesTags: ['WorkflowCustomization'],
    }),

    addWorkflowStage: builder.mutation<WorkflowCustomizationCreated, { workflowId?: string;  name: string; description?: string }>({
      query: ({ workflowId, name, description }) => ({
        url: `/api/v1/workflows/customization/stages${wfQuery(workflowId)}`,
        method: 'POST',
        body: { name, description },
      }),
      transformResponse: (res: ApiResponse<WorkflowCustomizationCreated>) => res.data,
      invalidatesTags: ['WorkflowCustomization'],
    }),

    addWorkflowProcess: builder.mutation<
      WorkflowCustomizationCreated,
      { workflowId?: string;  stageCode: string; name: string; description?: string }
    >({
      query: ({ workflowId, stageCode, name, description }) => ({
        url: `/api/v1/workflows/customization/processes${wfQuery(workflowId)}`,
        method: 'POST',
        body: { stage_code: stageCode, name, description },
      }),
      transformResponse: (res: ApiResponse<WorkflowCustomizationCreated>) => res.data,
      invalidatesTags: ['WorkflowCustomization'],
    }),

    addWorkflowStatus: builder.mutation<
      WorkflowCustomizationCreated,
      { workflowId?: string;  processCode: string; name: string; lifecycleRole?: string; operationalType?: string; description?: string }
    >({
      query: ({ workflowId, processCode, name, lifecycleRole, operationalType, description }) => ({
        url: `/api/v1/workflows/customization/statuses${wfQuery(workflowId)}`,
        method: 'POST',
        body: {
          process_code: processCode, name,
          lifecycle_role: lifecycleRole, operational_type: operationalType, description,
        },
      }),
      transformResponse: (res: ApiResponse<WorkflowCustomizationCreated>) => res.data,
      invalidatesTags: ['WorkflowCustomization'],
    }),

    addWorkflowTransition: builder.mutation<
      WorkflowCustomizationCreated,
      { workflowId?: string; 
        sourceStatusCode: string; targetStatusCode: string; actionLabel: string;
        category?: string; requiresReason?: boolean; allowedRoles: string[];
      }
    >({
      query: ({ workflowId, sourceStatusCode, targetStatusCode, actionLabel, category, requiresReason, allowedRoles }) => ({
        url: `/api/v1/workflows/customization/transitions${wfQuery(workflowId)}`,
        method: 'POST',
        body: {
          source_status_code: sourceStatusCode, target_status_code: targetStatusCode,
          action_label: actionLabel, category, requires_reason: requiresReason, allowed_roles: allowedRoles,
        },
      }),
      transformResponse: (res: ApiResponse<WorkflowCustomizationCreated>) => res.data,
      invalidatesTags: ['WorkflowCustomization'],
    }),

    setTransitionAuthority: builder.mutation<
      WorkflowCustomizationOut,
      { workflowId?: string;  transitionCode: string; allowedRoles: string[] }
    >({
      query: ({ workflowId, transitionCode, allowedRoles }) => ({
        url: `/api/v1/workflows/customization/transitions/${transitionCode}/authority${wfQuery(workflowId)}`,
        method: 'POST',
        body: { allowed_roles: allowedRoles },
      }),
      transformResponse: (res: ApiResponse<WorkflowCustomizationOut>) => res.data,
      invalidatesTags: ['WorkflowCustomization'],
    }),

    setGateApprovalSettings: builder.mutation<
      WorkflowCustomizationOut,
      { workflowId?: string;  gateCode: string; approverRole?: string | null; prohibitSelfApproval?: boolean }
    >({
      query: ({ workflowId, gateCode, approverRole, prohibitSelfApproval }) => ({
        url: `/api/v1/workflows/customization/gates/${gateCode}/approval${wfQuery(workflowId)}`,
        method: 'POST',
        body: { approver_role: approverRole, prohibit_self_approval: prohibitSelfApproval ?? false },
      }),
      transformResponse: (res: ApiResponse<WorkflowCustomizationOut>) => res.data,
      invalidatesTags: ['WorkflowCustomization'],
    }),

    // Cascading access policy at scope="default" | "stage" | "process" — narrows on top of
    // whatever a broader scope already restricts (see app.domain.workflows.service
    // WorkflowService._effective_access_policy). scopeCode is a stage_code/process_code,
    // omitted when scope="default".
    setWorkflowAccessPolicy: builder.mutation<
      WorkflowCustomizationOut,
      { workflowId?: string;  scope: 'default' | 'stage' | 'process'; scopeCode?: string; allowedRoles: string[]; assignmentRequired: boolean }
    >({
      query: ({ workflowId, scope, scopeCode, allowedRoles, assignmentRequired }) => ({
        url: `/api/v1/workflows/customization/access-policy${wfQuery(workflowId)}`,
        method: 'POST',
        body: { scope, scope_code: scopeCode, allowed_roles: allowedRoles, assignment_required: assignmentRequired },
      }),
      transformResponse: (res: ApiResponse<WorkflowCustomizationOut>) => res.data,
      invalidatesTags: ['WorkflowCustomization'],
    }),

    // Resolve role_lookup options for a required_data capture field — fetches active tenant
    // members holding the given system role code (e.g. "project_manager", "field_crew").
    previewGovernanceOptions: builder.query<{ available: boolean; options: Array<{ value: string; label: string }> }, string>({
      query: (role) => `/api/v1/workflows/governance-options/preview?role=${encodeURIComponent(role)}`,
      transformResponse: (res: ApiResponse<{ available: boolean; options: Array<{ value: string; label: string }> }>) => res.data,
    }),
  }),
});

export const {
  useListEngagementsQuery,
  useGetEngagementByIdQuery,
  useGetEngagementActivityQuery,
  useCreateEngagementMutation,
  useUpdateEngagementDataMutation,
  useGetEngagementWorkflowStateQuery,
  useListAvailableTransitionsQuery,
  useGetWorkflowStatusNarrationQuery,
  useExecuteWorkflowTransitionMutation,
  useListPendingApprovalsQuery,
  useListEngagementApprovalsQuery,
  useGetApprovalByIdQuery,
  useDecideApprovalMutation,
  useReassignApprovalMutation,
  useGetWorkflowCustomizationQuery,
  useListConfigurableWorkflowsQuery,
  useToggleWorkflowItemMutation,
  useRenameWorkflowLabelMutation,
  useAddWorkflowStageMutation,
  useAddWorkflowProcessMutation,
  useAddWorkflowStatusMutation,
  useAddWorkflowTransitionMutation,
  useSetTransitionAuthorityMutation,
  useSetGateApprovalSettingsMutation,
  useSetWorkflowAccessPolicyMutation,
  useSetEngagementAccessPolicyMutation,
  useReassignEngagementHolderMutation,
  usePutEngagementOnHoldMutation,
  useResumeEngagementMutation,
  useCloseEngagementEarlyMutation,
  usePreviewGovernanceOptionsQuery,
} = workflowEngineApi;
